import { check } from 'meteor/check';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { Random } from 'meteor/random';
import { DeclareMethod } from '/imports/methods';
import { currentUserId, escapeRegExp, filterBoolean, uniqueArray } from '/imports/utils';

export type LogLevel = 'trace' | 'info' | 'warning' | 'error' | 'critical';

export const logLevelRank: { [k in LogLevel]: number } = { trace: 0, info: 1, warning: 2, error: 3, critical: 4 };

// The floor below which a log is not written at all, neither to the collection nor to stdout. Absent means
// write everything, which is what every environment did before this existed. It is the lever for the
// volume the trace/info instrumentation adds: set LOG_LEVEL=info on a busy environment to drop traces
// without a code change, or LOG_LEVEL=warning to keep only what someone would act on.
const logFloor = (): number => {
  const configured = (process.env.LOG_LEVEL || '').trim().toLowerCase() as LogLevel;
  return logLevelRank[configured] ?? logLevelRank.trace;
};

export interface Log {
  _id: string;
  level: LogLevel;
  message: string;
  data?: any;
  extra?: any;
  orgId?: string;
  timestamp: Date,
  clientTimestamp?: Date,
}

export const Logs = new Mongo.Collection<Log>('logs');

// TTL window after which logs auto-expire, bounding the high-volume collection.
const LOG_TTL_SECONDS = (Number(process.env.LOG_TTL_DAYS) || 30) * 24 * 3600;

Meteor.isServer && Meteor.startup(async () => {
  await Logs.createIndexAsync({ level: 1 });
  // Recreating a `timestamp_1` index with a changed TTL requires dropping the existing one first.
  const raw = Logs.rawCollection();
  try {
    await raw.createIndex({ timestamp: 1 }, { expireAfterSeconds: LOG_TTL_SECONDS });
  }
  catch (_) {
    try {
      await raw.dropIndex('timestamp_1');
      await raw.createIndex({ timestamp: 1 }, { expireAfterSeconds: LOG_TTL_SECONDS });
    }
    catch (error: any) {
      console.log('logs TTL index setup failed', `${error}`);
    }
  }
  await Logs.createIndexAsync({ 'extra.tags': 1 }, { sparse: true });
});

export type ListLogsArg = {
  limit: number,
  tags?: string[],
  levels?: string[],
  search?: string,   // substring of the message, case-insensitive
}
/** List recent log entries filtered by level/tags. */
export const listLogs = DeclareMethod<ListLogsArg, Log[]>('listLogs');

/** Fetch the stored `data` payload for a single log entry by id. */
export const logData = DeclareMethod<string, any>('logData');

/**
 * Build a Mongo query matching logs whose message contains the search term.
 * @param search - free-text term; blank yields an empty (match-all) query
 * @returns Mongo selector
 */
export const logSearchQuery = (search?: string) => {
  search = search?.trim();
  if (!search?.length) return {};
  // Escaped: the term is typed by a person, and an unbalanced bracket would otherwise throw inside the
  // method rather than simply match nothing.
  return {
    $or: [
      { message: new RegExp(escapeRegExp(search), 'i') },
    ]
  };
};

/**
 * Whether a user may list logs (super users only).
 * @param user - the user to check
 * @returns true if permitted
 */
export const canListLogs = (user?: Meteor.User | null) => {
  if (!user) return false;
  if (user?.isSuperUser) return true;
  return false;
};

/**
 * Write a log entry to console and the Logs collection (fire-and-forget, w:0).
 * @param level - severity
 * @param message - human-readable message
 * @param data - optional structured payload
 * @param extra - optional userId/orgId/tags/data metadata
 * @returns the insert promise; below LOG_LEVEL an unacknowledged result, with nothing written
 */
export const log = (
  level: LogLevel,
  message: string,
  data?: any,
  extra?: {
    userId?: string,
    orgId?: string,
    tags?: string[],
    data?: any,
  }
) => {
  const timestamp = new Date();
  const _id = Random.id();
  const userId = currentUserId();
  // Below the floor nothing is written or printed. The shape of the result is unchanged so that callers
  // keep type-checking; `acknowledged: false` is what says the entry never reached the collection.
  if (logLevelRank[level] < logFloor()) return Promise.resolve({ acknowledged: false, insertedId: _id as any });
  console.log(`${timestamp.toISOString()} [${level}] ${message}${data ? `: ${JSON.stringify(data)}` : ''}`);
  extra = extra || {};
  if (userId) {
    extra.tags = extra.tags || [];
    extra.tags.push(`u-${userId}`);
  }
  if (extra.tags) extra.tags = uniqueArray(extra.tags);
  return Logs.rawCollection().insertOne({
    _id, level, message, data, extra, timestamp,
  }, { writeConcern: { w: 0 } });
};

/**
 * Build cursor params (query/sort/limit/fields) for listing logs.
 * @param arg - limit and optional level/tag filters
 * @returns query, sort, limit, and field projection
 */
export const listLogsCursorParams = (arg: ListLogsArg) => {
  const { limit, levels, tags, search } = arg || {};
  limit && check(limit, Number);
  tags && check(tags, [String]);
  levels && check(levels, [String]);
  search && check(search, String);
  const query: any = { $and: [{}] };
  const _tags = tags ? uniqueArray(filterBoolean(tags)) : undefined;
  if (_tags?.length) query.$and.push({ 'extra.tags': { $all: _tags } });
  query.$and.push({ level: levels?.length ? { $in: levels } : { $exists: true } });
  if (search?.trim()) query.$and.push(logSearchQuery(search));
  const sort = { timestamp: -1 };
  const fields = { data: 0 };
  return { query, sort, limit, fields };
};